import React, { useState, useEffect } from "react";
import api from "./axiosConfig";
import { useNavigate } from "react-router-dom";
import Icon from "./components/Icon";
import showIcon from "./assets/icons/visibility.svg";
import hideIcon from "./assets/icons/visibility-off.svg";
import UserDropdown from "./components/UserDropdown";
import "./AdminPage.css"; 
import SideMenu from "./components/SideMenu";
import Select from 'react-select';

const PUBLIC_KEY = `-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAne/6S5L52q4KXeCy3gqo
cCuPiZzVWjg9v8J7f8dbHhWuO/WsWJfc2AC7Y82lgKK19gg9wYN4h4r+J7mNpvjI
3YoTxm215O628iSzqWdBKTaBif2pU6Ksp7dcvMQk5sKQzuy7dXEBY+j56cDznnnI
41RAHnOyEIO+5Nz2oDGUfRMyIjAGI4eOQqz75oD4ZpESbPXpUr0RamtnLECzp1TV
eLtuqds8qQw2zwipq0lwntHq2gPKtNxPbuNPfRqHcewDm4qebJO0yQBy+kvcaNGR
B9K5JFThER6PlWx8Ng2Aov4MW6AtUwCyw4YghWO4NPXQtXsh3hPpqAn263/9RjnT
rwIDAQAB
-----END PUBLIC KEY-----`;

const importPublicKey = async (publicKeyBase64) => {
  const binaryDer = Uint8Array.from(atob(publicKeyBase64), (c) => c.charCodeAt(0));

  return await window.crypto.subtle.importKey(
    "spki", // Key format
    binaryDer,
    {
      name: "RSA-OAEP",
      hash: "SHA-256",
    },
    true,
    ["encrypt"]
  );
};

function AdminPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("employee");
  const [fullname, setFullname] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(""); 
  const [showPassword, setShowPassword] = useState(false);

  const [userDropdown, setUserDropdown] = useState([]);
  const [groupDropdown, setGroupDropdown] = useState([]);
  const [refreshUsers, setRefreshUsers] = useState(false);

  const navigate = useNavigate();

  const token = localStorage.getItem("accessToken");

  useEffect(() => {
    const fetchUsers = async () => {
        try {
            const response = await api.get("/user/dropdown", { 
                headers: { Authorization: token }
            });
            setUserDropdown(response.data || []);
        } catch (error) {
            console.error("Error fetching users:", error);
        }
    };
    fetchUsers();
}, [token, refreshUsers]);

useEffect(() => {
  const fetchGroups = async () => {
      try {
          const response = await api.get("/group/dropdown", { 
              headers: { Authorization: token }
          });
          setGroupDropdown(response.data || []);
      } catch (error) {
          console.error("Error fetching users:", error);
      }
  };
  fetchGroups();
}, [token]);

  const handleCreateEmployee = async () => {
    const trimmedUsername = username.trim();
    const trimmedFullname = fullname.trim();
    const trimmedPassword = password.trim();
    
    const publicKeyBase64 = PUBLIC_KEY.replace(/-----(BEGIN|END) PUBLIC KEY-----/g, "").replace(/\n/g, "");

    // Import the public key
    const publicKey = await importPublicKey(publicKeyBase64);

    // Convert password to Uint8Array
    const encoder = new TextEncoder();
    const encodedPassword = encoder.encode(trimmedPassword);

    // Encrypt the password
    const encryptedPassword = await window.crypto.subtle.encrypt(
      {
        name: "RSA-OAEP",
      },
      publicKey,
      encodedPassword
    );

    // Convert encrypted data to Base64 for transmission
    const encryptedPasswordBase64 = btoa(
      String.fromCharCode(...new Uint8Array(encryptedPassword))
    );

    try {
      await api.post(
        "/user/create",
        { username: trimmedUsername, password: encryptedPasswordBase64, role, fullname: trimmedFullname },
        { headers: { Authorization: token } }
      );
      setMessage(`Employee created.`);
      setMessageType("success");
      setUsername("");
      setFullname("");
      setPassword("");
      setRefreshUsers(prev => !prev);
    } catch (err) {
      setMessage(err.response?.data?.error || "Failed to create employee.");
      setMessageType("error"); // Set message type to error
    }
  };

  const handleLogout = async () => {
    localStorage.removeItem("accessToken");
    navigate("/login"); // Redirect to login
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword); // Toggle visibility
  };

  return (
    <>
    <SideMenu/>
    <div className="admin-page">
      <div className="header-title">
        <h1>Admin Page - Create Employee</h1>
      </div>
      <button onClick={handleLogout} className="logout-button">
        Logout
      </button>
      <div className="form-wrapper">
        <div className="form-group">
          <label htmlFor="username">Username<span className="required">*</span></label>
          <input
            type="text"
            id="username"
            placeholder="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="form-input"
          />
        </div>
        <div className="form-group">
          <label htmlFor="fullname">Full Name</label>
          <input
            type="text"
            id="fullname"
            placeholder="Full Name"
            value={fullname}
            onChange={(e) => setFullname(e.target.value)}
            className="form-input"
          />
        </div>
        <div className="form-group">
          <label htmlFor="password">Password<span className="required">*</span></label>
          <div className="password-input-wrapper">
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="form-input"
            />
            {password ? 
            <button
              type="button"
              onClick={togglePasswordVisibility}
              className="show-password-button"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              <Icon src={showPassword ? hideIcon : showIcon} alt="Toggle password visibility" />
            </button> : ""}
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="role">Role</label>
          <select
            id="role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="form-input"
          >
            <option value="employee">Employee</option>
            <option value="admin">Admin</option>
          </select>
        </div>

        <div className="form-group">
            <label htmlFor="groupDropdown">Select Group (Optional)</label>
            <Select
                id="groupDropdown"
                closeMenuOnSelect={true}
                value={groupDropdown.find(e => e.value === selectedGroup)} 
                options={groupDropdown.map(e => ({value: e.id, label: e.name}))}
                onChange={(e) => setSelectedGroup(e.value)}
                isSearchable
                isClearable
                className="react-select-container form-input"
                classNamePrefix="react-select"
            />
        </div>

        <button onClick={handleCreateEmployee} className="create-button">
          Create Employee
        </button>
        {message && (
          <p className={`message ${messageType === "error" ? "error" : ""}`}>
            {message}
          </p>
        )}
      </div>
    </div>
    <UserDropdown users={userDropdown} groupDropdown={groupDropdown}/>
    </>
  );
}

export default AdminPage;